import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import useSeo from "../lib/useSeo";

export default function LoginPage() {
  useSeo({
    title: "Sign in — Plate Buddies",
    description: "Sign in to save your weekly meal plan and shopping list.",
    url: "https://platebuddies.com/login",
    noindex: true,
  });

  const { user, exchangeSessionId, loginUrl } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [exchanging, setExchanging] = useState(false);

  // Emergent redirects back with #session_id=<sid> in the URL fragment
  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    setExchanging(true);
    exchangeSessionId(m[1])
      .then(() => {
        window.history.replaceState({}, "", location.pathname);
        toast.success("You're in.");
        navigate("/planner");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Sign-in didn't complete. Try once more?");
        setExchanging(false);
      });
  }, [exchangeSessionId, location.pathname, navigate]);

  // If already signed in, hop straight to the planner
  useEffect(() => {
    if (user && !exchanging) navigate("/planner");
  }, [user, exchanging, navigate]);

  return (
    <div className="min-h-screen bg-pb-brand-purple flex flex-col">
      <header className="max-w-5xl mx-auto w-full px-5 sm:px-8 pt-6 flex items-center">
        <Link to="/home" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition text-sm font-bold">
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5">
        <div
          className="w-full max-w-md rounded-[2.5rem] bg-white shadow-[0_30px_80px_rgba(0,0,0,0.35)] border border-white/10 p-10 sm:p-12 text-center"
          data-testid="login-card"
        >
          {/* Logo sits in a Deep Purple sticker inside the white card so the
              dark-background logo stays legible without turning the entire
              sign-in form dark (keeps the Google button + helper copy clear). */}
          <span className="inline-flex items-center bg-pb-brand-purple rounded-[1.25rem] px-5 py-3 shadow-[0_10px_24px_rgba(78,44,128,0.3)]">
            <img src="/brand/logo-wordmark.png" alt="Plate Buddies" className="h-11 w-auto" />
          </span>

          <h1 className="mt-6 font-display font-black text-4xl text-pb-ink leading-tight">
            Sign in to plan the week.
          </h1>
          <p className="mt-4 text-pb-soft leading-relaxed">
            Sign in with Google to save your meal plan, get a categorized shopping list, and email the plan to yourself.
          </p>

          {exchanging ? (
            <div className="mt-8 inline-flex items-center gap-2 text-pb-soft">
              <Loader2 className="h-5 w-5 animate-spin" /> Signing you in…
            </div>
          ) : (
            <a
              href={loginUrl}
              data-testid="btn-google-signin"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-pb-ink text-white font-black text-base px-7 py-3.5 shadow-[0_14px_32px_rgba(28,22,40,0.25)] hover:bg-pb-coral hover:scale-105 transition"
            >
              <LogIn className="h-5 w-5" />
              Continue with Google
            </a>
          )}

          <p className="mt-8 text-xs text-pb-soft/80 leading-relaxed">
            We use Google only to know it's you. We don&rsquo;t post anything, and we never share your email.
          </p>
        </div>
      </main>

      <footer className="max-w-5xl mx-auto w-full px-5 sm:px-8 py-8 text-center text-[11px] text-white/50">
        &copy; 2026 Plate Buddies
      </footer>
    </div>
  );
}
